import { inject, Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  EdroNetworkErrorCode,
  EdroNetworkState,
} from '../helper/enum/EdroNetworkState';
import { IMasterSync } from '../products/IMasterSync';
import { MQTTNetworkManager } from './mqtt-network-manager';
import { IProduct, IProductFunctionnality } from '../products/IProduct';
import {
  getPoolConnectClass,
  getPoolProductsWithCloudHash,
  getPoolProductsWithCloudUpdate,
  getProductByModelIdentifier,
  setBrandByPool,
} from '../ECoSystemsHelper';
import { Pool, PoolApi } from '../../../apis/pool.api';
import { getSyncManagerByModelIdentifierAndVersion } from '../products/SyncFactory';
import { makeid, wait } from '../helper/api-helper';
import { FirmwareApi } from '../../../apis/productFirmware.api';
import { UserContextService } from '../../user-context.service';
import { User } from '../../../apis/profil.api';
import { ModalController } from '@ionic/angular/standalone';
import { PoolEditModalComponent } from '../../../pages/home/pool/modals/pool-edit-modal/pool-edit-modal.component';
import { SlideModalController } from '../../slide-modal.service';
import { FunctionModalProductComponent } from '../../../components/ecosystems/function-modal-product/function-modal-product.component';
import { GlobalModalRemoveComponent } from '../../../components/global-modal-remove/global-modal-remove.component';
import { NotConnectedModalComponent } from '../../../components/ecosystems/not-connected-modal/not-connected-modal.component';
import { AppComponent } from '../../../app.component';

export type PoolContextData = {
  pool?: Pool | null;
  networkState: {
    state: EdroNetworkState;
    mqttState: EdroNetworkState;
    bleState: EdroNetworkState;
  };
  isSync: boolean;
};

export enum FunctionOnglet {
  CONTROL = 'control',
  PARAMETRE_REGLAGE = 'settings',
  STATUS = 'status',
}

export const defaultPoolContext: PoolContextData = {
  pool: null,
  networkState: {
    state: EdroNetworkState.Stop,
    mqttState: EdroNetworkState.Stop,
    bleState: EdroNetworkState.Stop,
  },
  isSync: false,
};

@Injectable({
  providedIn: 'root',
})
export class EdroNetworkManager {
  public onServiceStateChange = new BehaviorSubject<string>('change');

  private poolContextData: PoolContextData = JSON.parse(
    JSON.stringify(defaultPoolContext),
  );
  private _masterSyncManager: IMasterSync | null = null;
  private static instance: EdroNetworkManager | null = null;

  private slideModalService: SlideModalController =
    inject(SlideModalController);
  private modalService: ModalController = inject(ModalController);
  private _mqttManager: MQTTNetworkManager = inject(MQTTNetworkManager);
  private _firmwareApi: FirmwareApi = inject(FirmwareApi);
  private _poolApi: PoolApi = inject(PoolApi);
  private dataChangeTimeout: any = null;
  private cloudUpdateFirmwares: IProduct[] | null = null;
  private cloudUpdateFirmwaresHash: string | null = null;

  public deviceActionStackSingleton: any = {};

  constructor() {
    if (EdroNetworkManager.instance) {
      return EdroNetworkManager.instance;
    }

    EdroNetworkManager.instance = this;

    this._mqttManager.setEdroNetworkManager(this);

    // Abonnement aux changements d'état MQTT
    this._mqttManager.onServiceStateChange.subscribe(() => {
      this.checkNetworkProcess();
      this.sendEmitEvent();
    });

    this._mqttManager.onDataChange.subscribe(() => {
      this.checkNetworkProcess();
      this.sendEmitEvent();
    });
  }

  getUserContext(): { user: User } {
    return {
      user: UserContextService.staticCurrentUser as User,
    };
  }

  async openProductModal(
    productClass: IProduct,
    functionnality: IProductFunctionnality,
    onglet: FunctionOnglet,
    title: string,
  ) {
    const modal = await this.slideModalService.create({
      component: FunctionModalProductComponent,
      componentProps: {
        productClass: productClass,
        functionnality: functionnality,
        onglet: onglet,
        title: title,
      },
    });

    await modal.present();
  }

  static getInstance(): EdroNetworkManager {
    return this.instance as EdroNetworkManager;
  }

  public getCloudUpdateFirmwares() {
    return this.cloudUpdateFirmwares;
  }

  get mqttManager(): MQTTNetworkManager | null {
    return this._mqttManager;
  }

  private sendEmitEvent() {
    if (this.dataChangeTimeout) {
      clearTimeout(this.dataChangeTimeout);
    }

    this.dataChangeTimeout = setTimeout(() => {
      this.checkNeedCheckFirmware();
      this.onServiceStateChange.next('change');
    }, 100);
  }

  private checkNeedCheckFirmware() {
    const hash = getPoolProductsWithCloudHash(
      this.poolContextData!.pool!,
      this.poolContextData,
    );

    if (hash != this.cloudUpdateFirmwaresHash) {
      getPoolProductsWithCloudUpdate(
        this.poolContextData!.pool!,
        this._firmwareApi,
      )
        .then((res) => {
          this.cloudUpdateFirmwares = res;
          this.cloudUpdateFirmwaresHash = hash;
        })
        .catch(() => {
          this.cloudUpdateFirmwares = [];
          this.cloudUpdateFirmwaresHash = hash;
        });
    }

    this.cloudUpdateFirmwaresHash = hash;
  }

  private preparePool(pool: Pool): Pool {
    this.setMasterSyncManager(null);
    this.poolContextData.isSync = false;

    if (
      pool &&
      pool.deviceTable &&
      pool.deviceTable.master &&
      pool.deviceTable.master.modelIdentifier
    ) {
      const syncManager = getSyncManagerByModelIdentifierAndVersion(
        pool.deviceTable.master.modelIdentifier,
        pool.deviceTable.master.firmwareVersion,
      );

      if (syncManager) {
        syncManager.initSyncMode(pool!);
      }

      this.setMasterSyncManager(syncManager);
    }

    return pool;
  }

  public async setPool(pool: Pool, force: boolean = false) {
    if (
      force === false &&
      pool.id &&
      pool.id === this.poolContextData!.pool?.id
    ) {
      // Si la piscine est déjà sélectionnée, on ne va peut-être pas tenter de refresh à nouveau
      return Promise.resolve();
    }

    if (pool.id) {
      console.log('SET POOL' + pool.id);
      let p = null;

      try {
        p = await this._poolApi.getPool(pool.id!);
      } catch (e) {
        console.log(e);
        this.poolContextData!.pool = this.preparePool({
          name: '',
          address: '',
        } as any);
        await this.reset();
        this.onServiceStateChange.next('change');
        throw e;
      }

      this.poolContextData!.pool = this.preparePool(p);
      await this.reset();

      this.onServiceStateChange.next('change');

      //TODO
      /*
      this.connect()
        .then(() => {})
        .catch((e) => {
          console.log(e);
        });*/
    } else {
      console.log('SET POOL NO ID');

      this.poolContextData!.pool = this.preparePool(pool);
      await this.reset();

      this.onServiceStateChange.next('change');
      console.log(this.poolContextData!.pool);
    }
  }

  public async updatePool(pool: Pool) {
    pool.deviceTable = this.poolContextData!.pool?.deviceTable;
    this.poolContextData!.pool = { ...pool };
    this.onServiceStateChange.next('change');
  }

  private connectionProcessLock: boolean = false;
  private connectionTicket: string | null = null;

  private async connectionProcess(
    promiseToBeSafe: () => Promise<any>,
  ): Promise<any> {
    const ticket = makeid(10);
    console.log('connectionProcess : ' + ticket);

    if (this.connectionProcessLock) {
      this.connectionTicket = ticket;

      let count = 0;
      while (this.connectionProcessLock) {
        if (this.connectionTicket != ticket) {
          console.log(
            'connectionProcess : ' +
              ticket +
              ' ' +
              'Une autre demande de connexion est survenue',
          );
          return Promise.reject('Une autre demande de connexion est survenue');
        } else {
          await wait(100);
          count++;

          if (count >= 100) {
            console.log(
              'connectionProcess : ' + ticket + ' ' + 'Init Lock Timeout',
            );
            return Promise.reject('Init Lock Timeout');
          }
        }
      }

      if (this.connectionTicket != ticket) {
        console.log(
          'connectionProcess : ' +
            ticket +
            ' ' +
            'Une autre demande de connexion est survenue',
        );
        return Promise.reject('Une autre demande de connexion est survenue');
      }

      this.connectionProcessLock = true;
      await this.reset();
    } else {
      this.connectionProcessLock = true;
    }

    console.log('connectionProcess run : ' + ticket);
    this.mqttManager!.stopRunningProcess = false;

    return new Promise<any>((resolve, reject) => {
      promiseToBeSafe()
        .then((r) => {
          resolve(r);
        })
        .catch((r) => {
          reject(r);
        })
        .finally(() => {
          this.connectionProcessLock = false;
        });
    });
  }

  public async reset() {
    // Reset de la demande de ticket
    this.connectionTicket = null;
    this.mqttManager!.stopRunningProcess = true;

    return this.mqttReset();
  }

  public async connect(timeout: number = 10000): Promise<boolean> {
    return this.connectionProcess(() => this.safeConnect(timeout));
  }

  private async safeConnect(timeout: number = 10000): Promise<boolean> {
    const user = UserContextService.staticCurrentUser;

    if (!user) {
      return Promise.reject('No user data');
    }

    if (
      !this.poolContextData!.pool ||
      !this.poolContextData!.pool!.deviceTable ||
      !this.poolContextData!.pool!.deviceTable!.master ||
      !this.poolContextData!.pool!.deviceTable!.master!.sharedKey
    ) {
      return Promise.reject('No user or no pool or no sharedKey known');
    }

    if (!this.poolContextData!.pool) {
      return Promise.reject('No pool data');
    }

    // Si on est déjà connecté, pas besoin de relancer une connexion
    if (this.poolContextData?.networkState?.state == EdroNetworkState.Running) {
      return Promise.resolve(true);
    }

    if (getPoolConnectClass(this.poolContextData.pool) != null) {
      try {
        await this.safeMqttConnect();
      } catch (error) {
        // En mode web, on a seulement MQTT
        if (this.connectionTicket == null) {
          console.log(error);
          return Promise.reject('MQTT connection failed');
        }
      }
    } else {
      return Promise.reject('No connection method available');
    }

    return Promise.resolve(true);
  }

  public async mqttReset() {
    await this._mqttManager!.reset().catch((e) => {
      console.log(e);
    });
  }

  private async safeMqttConnect(): Promise<boolean> {
    if (this.mqttManager?.getServiceState() == EdroNetworkState.Running) {
      return Promise.resolve(true);
    }

    await this.mqttReset();

    const user = UserContextService.staticCurrentUser;

    if (!user) {
      return Promise.reject('No user data');
    }

    if (!this.poolContextData!.pool) {
      return Promise.reject('No pool data');
    }

    await this._mqttManager!.init();

    return Promise.resolve(true);
  }

  public async askDataWrite(
    productClass: IProduct,
    index: any,
    pool: Pool,
  ): Promise<true> {
    await this.openConnectModalIfNotConnected();

    if (this.mqttManager?.getServiceState() == EdroNetworkState.Running) {
      return this._mqttManager!.askDataWrite(productClass, index, pool);
    }

    return Promise.reject('EdroNetworkState not running');
  }

  async openConnectModalIfNotConnected(): Promise<boolean> {
    if (this.mqttManager?.getServiceState() !== EdroNetworkState.Running) {
      const modal = await this.modalService.create({
        component: NotConnectedModalComponent,
        cssClass: 'dynamic-height',
        animated: false,
      });

      await modal.present();
      return (await modal.onDidDismiss()).data as boolean;
    }

    return Promise.resolve(true);
  }

  getPoolContext(): PoolContextData {
    return this.poolContextData;
  }

  getMasterSyncManager(): IMasterSync | null {
    return this._masterSyncManager;
  }

  setMasterSyncManager(value: IMasterSync | null) {
    this._masterSyncManager = value;
  }

  checkNetworkProcess() {
    this.poolContextData.networkState.mqttState =
      EdroNetworkManager.getInstance().mqttManager!.getServiceState();
    // En mode web, pas de Bluetooth
    this.poolContextData.networkState.bleState = EdroNetworkState.Stop;

    this.poolContextData.networkState.state =
      this.poolContextData.networkState.mqttState;
    this.poolContextData.isSync = this._masterSyncManager
      ? this._masterSyncManager.isSync
      : false;
  }
}

// Hook personnalisé adapté pour Angular (à utiliser dans les composants)
export class DeviceActionStackService {
  private actionStack: any = {};
  private deviceActionStackId: string;
  private poolData: PoolContextData;

  constructor(
    private productClass: IProduct,
    private edroNetworkManager: EdroNetworkManager,
    initActionStack: any = null,
  ) {
    this.deviceActionStackId = makeid(10);
    this.actionStack = initActionStack
      ? JSON.parse(JSON.stringify(initActionStack))
      : {};
    this.poolData = edroNetworkManager.getPoolContext();

    if (
      !this.edroNetworkManager.deviceActionStackSingleton[
        this.deviceActionStackId
      ]
    ) {
      this.edroNetworkManager.deviceActionStackSingleton[
        this.deviceActionStackId
      ] = this.actionStack;
    }

    this.getValue = this.getValue.bind(this);
    this.commit = this.commit.bind(this);
    this.commitWithPromise = this.commitWithPromise.bind(this);
    this.editValue = this.editValue.bind(this);
    this.editValues = this.editValues.bind(this);
    this.removeValue = this.removeValue.bind(this);
    this.getActionStack = this.getActionStack.bind(this);
  }

  getValue(property: any, productClassOverride: IProduct | null = null): any {
    const dataIdentifier =
      productClassOverride != null
        ? productClassOverride.dataIdentifier
        : this.productClass.dataIdentifier;
    const propertyWithProductClass = dataIdentifier + '.data.' + property;

    if (
      this.actionStack[propertyWithProductClass] !== undefined &&
      this.actionStack[propertyWithProductClass] !== null
    ) {
      return this.actionStack[propertyWithProductClass].value;
    }

    const keys: string[] = propertyWithProductClass.split('.');
    let tmp: any = this.poolData?.pool?.deviceTable;

    if (!tmp) {
      return null;
    }

    for (let i = 0; i < keys.length; i++) {
      if (tmp == null) return tmp;
      tmp = tmp[keys[i]];
    }

    return tmp;
  }

  commit(): boolean {
    const p = JSON.parse(JSON.stringify(this.poolData!.pool));

    const keys: string[] = Object.keys(this.actionStack);
    const indexs: number[] = [];
    const indexsModelIdentifier: any[] = [];

    for (let i = 0; i < keys.length; i++) {
      if (!indexs.includes(this.actionStack[keys[i]].index)) {
        indexs.push(this.actionStack[keys[i]].index);
        indexsModelIdentifier.push(this.actionStack[keys[i]].modelIdentifier);
      }

      const keyProperties: string[] = keys[i].split('.');
      let tmp: any = p?.deviceTable;

      if (!tmp) {
        continue;
      }

      for (let j = 0; j < keyProperties.length - 1; j++) {
        tmp = tmp[keyProperties[j]];
      }

      tmp[keyProperties[keyProperties.length - 1]] =
        this.actionStack[keys[i]].value;
    }

    for (let i = 0; i < indexs.length; i++) {
      this.edroNetworkManager
        ?.askDataWrite(
          getProductByModelIdentifier(indexsModelIdentifier[i])!,
          indexs[i],
          p,
        )
        .then(() => {})
        .catch((e) => {
          console.log(e);
        });
    }

    return true;
  }

  async commitWithPromise(): Promise<boolean> {
    const p = JSON.parse(JSON.stringify(this.poolData!.pool));

    const keys: string[] = Object.keys(this.actionStack);
    const indexs: number[] = [];
    const indexsModelIdentifier: any[] = [];

    for (let i = 0; i < keys.length; i++) {
      if (!indexs.includes(this.actionStack[keys[i]].index)) {
        indexs.push(this.actionStack[keys[i]].index);
        indexsModelIdentifier.push(this.actionStack[keys[i]].modelIdentifier);
      }

      const keyProperties: string[] = keys[i].split('.');
      let tmp: any = p?.deviceTable;

      if (!tmp) {
        continue;
      }

      for (let j = 0; j < keyProperties.length - 1; j++) {
        tmp = tmp[keyProperties[j]];
      }

      tmp[keyProperties[keyProperties.length - 1]] =
        this.actionStack[keys[i]].value;
    }

    console.log(this.actionStack);

    for (let i = 0; i < indexs.length; i++) {
      // Ralentissement de l'envoi
      await new Promise((r) => setTimeout(r, 50));
      await this.edroNetworkManager?.askDataWrite(
        getProductByModelIdentifier(indexsModelIdentifier[i])!,
        indexs[i],
        p,
      );
    }

    return Promise.resolve(true);
  }

  private removeValue(idValue: number): boolean {
    const act =
      this.edroNetworkManager.deviceActionStackSingleton[
        this.deviceActionStackId
      ];
    const keys = Object.keys(act);
    for (let i = 0; i < keys.length; i++) {
      if (act[keys[i]].id === idValue) {
        delete act[keys[i]];
        this.actionStack = { ...act };
        return true;
      }
    }

    return true;
  }

  editValue(
    index: number,
    property: string,
    value: any,
    timeout: number | null = 1000,
    productClassOverride: IProduct | null = null,
  ): boolean {
    const dataIdentifier =
      productClassOverride != null
        ? productClassOverride.dataIdentifier
        : this.productClass.dataIdentifier;
    const modelIdentifier =
      productClassOverride != null
        ? productClassOverride.modelIdentifier
        : this.productClass.modelIdentifier;
    const propertyWithProductClass = dataIdentifier + '.data.' + property;

    const actionId = Date.now();

    let to = null;

    if (timeout) {
      to = setTimeout(() => {
        this.removeValue(actionId);
      }, timeout);
    }

    if (
      this.actionStack[propertyWithProductClass] &&
      this.actionStack[propertyWithProductClass].timeout
    ) {
      clearTimeout(this.actionStack[propertyWithProductClass].timeout);
    }

    this.actionStack[propertyWithProductClass] = {
      index: index,
      value: value,
      id: actionId,
      timeout: to,
      modelIdentifier: modelIdentifier,
    };

    this.edroNetworkManager.deviceActionStackSingleton[
      this.deviceActionStackId
    ] = this.actionStack;

    return true;
  }

  editValues(
    array: any,
    timeout: number = 1000,
    productClassOverride: IProduct | null = null,
  ): boolean {
    const dataIdentifier =
      productClassOverride != null
        ? productClassOverride.dataIdentifier
        : this.productClass.dataIdentifier;
    const modelIdentifier =
      productClassOverride != null
        ? productClassOverride.modelIdentifier
        : this.productClass.modelIdentifier;

    for (let i = 0; i < array.length; i++) {
      const propertyWithProductClass = dataIdentifier + '.data.' + array[i].key;

      const actionId = Date.now();

      let to = null;
      if (timeout) {
        to = setTimeout(() => {
          this.removeValue(actionId);
        }, timeout);
      }

      if (
        this.actionStack[propertyWithProductClass] &&
        this.actionStack[propertyWithProductClass].timeout
      ) {
        clearTimeout(this.actionStack[propertyWithProductClass].timeout);
      }

      this.actionStack[propertyWithProductClass] = {
        index: array[i].index,
        value: array[i].value,
        id: actionId,
        timeout: to,
        modelIdentifier: modelIdentifier,
      };
    }

    this.edroNetworkManager.deviceActionStackSingleton[
      this.deviceActionStackId
    ] = this.actionStack;

    return true;
  }

  getActionStack(): any {
    return this.actionStack;
  }
}
